/**
 * ErvaPOS - API Service
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getStoredAuthToken = () => {
  return (
    localStorage.getItem('ervapos_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('authToken') ||
    ''
  );
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token) {
    config.headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ervapos_token');
      localStorage.removeItem('ervapos_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  getUsers: () => api.get('/auth/users'),
  getPosUsers: () => api.get('/auth/pos-pin/users'),
  verifyPosPin: (email, pin) => api.post('/auth/pos-pin/verify', { email, pin }),
  setPosPin: (user_id, pin) => api.post('/auth/pos-pin/set', { user_id, pin }),
  setMyPosPin: (pin) => api.post('/auth/me/pos-pin', { pin }),
  resetPassword: (email, new_password) => api.post('/auth/reset-password', { email, new_password }),
    updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
  setUserActive: (user_id, is_active) => api.put(`/auth/users/${user_id}/active`, { is_active }),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  unlockGlobalView: (pin) => api.post('/auth/global-view/unlock', { pin }),
};

// Sinalização leve de alterações relevantes para terminais POS abertos.
export const posSyncAPI = {
  getVersion: () => api.get('/pos-sync/version'),
};

// Products API
export const productsAPI = {
  getAll: (params = {}) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getSalesHistory: (id) => api.get(`/products/${id}/sales-history`),
  saveSupplierReference: (id, supplierId, reference) => api.post('/invoices/map-product', { product_id: id, supplier_id: supplierId, invoice_ref: reference }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getCategories: () => api.get('/products/categories'),
};

// Stock Batches API
export const batchesAPI = {
  getAll: (params = {}) => api.get('/batches', { params }),
  getByQR: (qrCode) => api.get(`/batches/by-qr/${encodeURIComponent(qrCode)}`),
  getExpiring: (days = 90) => api.get('/batches/expiring', { params: { days } }),
  getPendingLabels: () => api.get('/batches/pending-labels'),
  create: (data) => api.post('/batches', data),
  correct: (id, data) => api.post(`/batches/${id}/correct`, data),
  getCorrections: (id) => api.get(`/batches/${id}/corrections`),
  adjust: (id, newQuantity, reason) => api.put(`/batches/${id}/adjust`, null, { params: { new_quantity: newQuantity, reason } }),
  delete: (id) => api.delete(`/batches/${id}`),
  update: (id, data) => api.put(`/batches/${id}`, data),
  markLabelsPrinted: (id) => api.put(`/batches/${id}/mark-labels-printed`),
};

export const stockWriteoffsAPI = {
  getAll: (params = {}) => api.get('/stock-writeoffs', { params }),
  create: (data) => api.post('/stock-writeoffs', data),
  getMonthlySummaryPDF: (month) => api.get('/stock-writeoffs/monthly-summary.pdf', {
    params: { month }, responseType: 'blob'
  }),
};

// Customers API
export const customersAPI = {
  getAll: (params = {}) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  getHistory: (id) => api.get(`/customers/${id}/history`),
    resolveByPhone: (phone) => api.get('/customers/resolve-by-phone', { params: { phone } }),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
};

// Vouchers API
export const providersAPI = {
  getAll: () => api.get('/providers'),
  getById: (id) => api.get(`/providers/${id}`),
  create: (data) => api.post('/providers', data),
  update: (id, data) => api.put(`/providers/${id}`, data),
  remove: (id) => api.delete(`/providers/${id}`),
  getFinancialSummary: () => api.get('/providers/financial-summary'),
  getStatement: (id) => api.get(`/providers/${id}/statement`),
  getPayments: (params = {}) => api.get('/providers/payments', { params }),
  createPayment: (data) => api.post('/providers/payments', data),
  createPaymentsBatch: (data) => api.post('/providers/payments/batch', data),
  updatePayment: (id, data) => api.put(`/providers/payments/${id}`, data),
};


// Customer Account API
export const customerAccountAPI = {
  getOpenItems: () => api.get('/customer-account/open-items'),
  getCustomerDetail: (customerId) => api.get(`/customer-account/customer/${customerId}`),
  registerPayment: (entryId, amount, method, note = '') =>
    api.post(`/customer-account/entries/${entryId}/register-payment`, null, {
      params: { amount, payment_method: method, note }
    }),
};

export const vouchersAPI = {
  getAll: (params = {}) => api.get('/vouchers', { params }),
  validate: (code) => api.get(`/vouchers/validate/${code}`),
  validateScan: (scanData) => api.post('/vouchers/validate-scan', { scan_data: scanData }),
  create: (data) => api.post('/vouchers', data),
  suspend: (id) => api.put(`/vouchers/${id}/suspend`),
  cancel: (id) => api.put(`/vouchers/${id}/cancel`),
  reactivate: (id) => api.put(`/vouchers/${id}/reactivate`),
  getPdf: (id) => api.get(`/vouchers/${id}/pdf`, {
    responseType: 'blob'
  }),
  sendEmail: (id, recipientEmail) => api.post(`/vouchers/${id}/send-email`, null, {
    params: { recipient_email: recipientEmail },
  }),
};

// Sales API
export const salesAPI = {
  getPaymentStatus: (id) => api.get(`/sales/${id}/payment-status`),
  updateInvoiceIdentity: (id, data) => api.put(`/sales/${id}/invoice-identity`, data),
  getAll: (params = {}) => api.get('/sales', { params }),
  searchVendusHistory: (q) => api.get('/sales/vendus-history/search', { params: { q } }),
  getById: (id) => api.get(`/sales/${id}`),
  getSuspended: () => api.get('/sales/suspended'),
  create: (data) => api.post('/sales', data),
  addItem: (saleId, item) => api.post(`/sales/${saleId}/add-item`, item),
  suspend: (id) => api.post(`/sales/${id}/suspend`),
  resume: (id) => api.post(`/sales/${id}/resume`),
  createPayment: (id, paymentPhone) => api.post(`/sales/${id}/create-payment`, null, {
    params: { payment_phone: paymentPhone }
  }),
  complete: (
    id,
    paymentMethod,
    invoiceType,
    voucherCode = null,
    pointsToUse = 0,
    cashReceived = null,
    cashChange = null,
    paymentBreakdown = null,
    prepaidMeta = null,
    accountCurrentMeta = null,
    confirmDuplicateSaleId = null,
    confirmServiceInternal = false
  ) => api.post(`/sales/${id}/complete`, null, {
    params: {
      payment_method: paymentMethod,
      invoice_type: invoiceType,
      voucher_code: voucherCode,
      points_to_use: pointsToUse,
      cash_received: cashReceived,
      cash_change: cashChange,
      payment_breakdown_json: paymentBreakdown ? JSON.stringify(paymentBreakdown) : null,
      prepaid_amount: prepaidMeta?.prepaid_amount ?? null,
      prepaid_method: prepaidMeta?.prepaid_method ?? null,
      account_current_paid_now: accountCurrentMeta?.paid_now ?? null,
      account_current_paid_method: accountCurrentMeta?.paid_method ?? null,
      confirm_duplicate_sale_id: confirmDuplicateSaleId,
      confirm_service_internal: confirmServiceInternal,
    }
  }),
  fiscalize: (id, registerId = null) => api.post(`/sales/${id}/fiscalize`, null, {
    params: { register_id: registerId }
  }),
  fiscalizeBatch: (saleIds, registerId) => api.post('/sales/fiscalize-batch', { sale_ids: saleIds, register_id: registerId }),
  updateDayClose: (id, data) => api.put(`/sales/${id}/day-close-edit`, data),
  excludeFromVendus: (id) => api.post(`/sales/${id}/exclude-from-vendus`),
  excludeFromVendusBatch: (saleIds) => api.post('/sales/exclude-from-vendus-batch', { sale_ids: saleIds }),
  getRecoverableVendusExclusions: () => api.get('/sales/excluded-from-vendus/recoverable'),
  restoreToDayClose: (id) => api.post(`/sales/${id}/restore-to-day-close`),
  getPendingFiscal: () => api.get('/sales', { params: { invoice_type: 'internal', status: 'completed' } }),
  getReceipt: (id) => api.get(`/sales/${id}/receipt`),
  getReceiptPDF: (id, format = 'receipt') => api.get(`/sales/${id}/receipt-pdf`, {
    params: { format },
    responseType: 'blob'
  }),
  getInvoicePDF: (id, format = 'a4') => api.get(`/sales/${id}/invoice-pdf`, {
    params: { format },
    responseType: 'blob'
  }),
  sendEmail: (id, recipientEmail) => api.post(`/sales/${id}/send-email`, null, {
    params: { recipient_email: recipientEmail },
  }),
  sendEmailWithPdf: (id, recipientEmail, pdfBase64, pdfFilename = 'talao.pdf') => api.post(
    `/sales/${id}/send-email`,
    {
      recipient_email: recipientEmail,
      pdf_base64: pdfBase64,
      pdf_filename: pdfFilename,
    }
  ),
  sendReceiptSms: (id, phone) => api.post(`/sales/${id}/send-receipt-sms`, { phone }),
  revokeReceiptLink: (id, shareId) => api.post(`/sales/${id}/receipt-links/${shareId}/revoke`),
};

// Suppliers API
export const suppliersAPI = {
  getAll: () => api.get("/suppliers/all"),
  getAllFull: () => api.get("/suppliers/all"),
  getById: (id) => api.get(`/suppliers/${id}`),
  getBalance: (id) => api.get(`/suppliers/${id}/balance`),
  getInvoices: (id) => api.get(`/suppliers/${id}/invoices`),
  create: (data) => api.post("/suppliers", data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};
export const supplierReturnsAPI = {
  getAll: () => api.get('/supplier-returns'),
  createDraft: (data) => api.post('/supplier-returns', data),
  updateDraft: (id, data) => api.put(`/supplier-returns/${id}`, data),
  issue: (id) => api.post(`/supplier-returns/${id}/issue`),
};
// Purchase Invoices API
export const invoicesAPI = {
  getAll: (params = {}) => api.get("/invoices", { params }),
  getDashboard: () => api.get("/invoices/dashboard"),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post("/invoices", data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  ocr: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/invoices/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } }); },
  createProductFromOCR: (data) => api.post("/invoices/create-product-from-ocr", data),
  registerPayment: (id, params) => api.post(`/invoices/${id}/payment`, null, { params }),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status`, null, { params: { status } }),
};

// Expenses API
export const expensesAPI = {
  getAll: (params = {}) => api.get('/expenses', { params }),
  getSummary: (params = {}) => api.get('/expenses/categories-summary', { params }),
  create: (data) => api.post('/expenses', data),
  markPaid: (id, paymentMethod) => api.put(`/expenses/${id}/pay`, null, { params: { payment_method: paymentMethod } }),
};

// Employees API
export const employeesAPI = {
  getAll: () => api.get('/employees'),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
};

// Timesheets API
export const timesheetsAPI = {
  getAll: (params = {}) => api.get('/timesheets', { params }),
  getSummary: (employeeId, month) => api.get(`/timesheets/summary/${employeeId}`, { params: { month } }),
  create: (data) => api.post('/timesheets', data),
  update: (id, data) => api.put(`/timesheets/${id}`, data),
};

// Inventory API
export const inventoryAPI = {
  getLocations: () => api.get('/inventory/locations'),
  createLocation: (data) => api.post('/inventory/locations', data),
  getMobileProduct: (scanCode) => api.get('/inventory/mobile/product', { params: { scan_code: scanCode } }),
  adjustMobileBatch: (batchId, data) => api.post(`/inventory/mobile/batches/${batchId}/adjust`, data),
  getAdjustmentsReport: (params = {}) => api.get('/inventory/adjustments/report', { params }),
  getSessions: () => api.get('/inventory/sessions'),
  getSession: (id) => api.get(`/inventory/sessions/${id}`),
  startSession: (data) => api.post('/inventory/sessions', data),
  scanItem: (sessionId, qrCode, quantity) =>
    api.post(`/inventory/sessions/${sessionId}/scan`, null, { params: { qr_code: qrCode, counted_quantity: quantity } }),
  completeSession: (id, applyAdjustments = false) =>
    api.post(`/inventory/sessions/${id}/complete`, null, { params: { apply_adjustments: applyAdjustments } }),

  // Enhanced inventory
  getStockOverview: () => api.get('/inventory/stock-overview'),
  startCounting: (name, notes = null) => 
    api.post('/inventory/sessions/start-counting', null, { params: { name, notes } }),
  scanItemV2: (sessionId, scanCode) =>
    api.post(`/inventory/sessions/${sessionId}/scan`, null, { params: { scan_code: scanCode } }),
  undoLastScan: (sessionId) => api.post(`/inventory/sessions/${sessionId}/undo-last`),
  finalizeSession: (sessionId, applyAdjustments = false, isPartial = false) =>
    api.post(`/inventory/sessions/${sessionId}/finalize`, null, { params: { apply_adjustments: applyAdjustments, is_partial: isPartial } }),
  getSessionStatus: (sessionId) => api.get(`/inventory/sessions/${sessionId}/status`),

    // Initial inventory
    getInitialSessions: () =>
      api.get('/inventory/initial-sessions'),
    startInitialSession: (name, notes = null) =>
      api.post('/inventory/initial-sessions/start', null, { params: { name, notes } }),
    getInitialSessionStatus: (sessionId) =>
      api.get(`/inventory/initial-sessions/${sessionId}/status`),
    resolveInitialProduct: (sessionId, scanCode) =>
      api.post(`/inventory/initial-sessions/${sessionId}/resolve-product`, null, { params: { scan_code: scanCode } }),
    saveInitialEntry: (sessionId, data) =>
      api.post(`/inventory/initial-sessions/${sessionId}/entries`, data),
    finalizeInitialSession: (sessionId, replaceExistingProductBatches = true) =>
      api.post(`/inventory/initial-sessions/${sessionId}/finalize`, null, {
        params: { replace_existing_product_batches: replaceExistingProductBatches }
      }),
};

// Campaigns API
export const campaignsAPI = {
  getAll: (activeOnly = true) => api.get('/campaigns', { params: { active_only: activeOnly } }),
  getActive: () => api.get('/campaigns', { params: { active_only: true } }),
  getById: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  generateVouchers: (id, quantity = 10) => api.post(`/campaigns/${id}/generate-vouchers`, null, { params: { quantity } }),
  suspend: (id) => api.put(`/campaigns/${id}/suspend`),
  cancel: (id) => api.put(`/campaigns/${id}/cancel`),
  reactivate: (id) => api.put(`/campaigns/${id}/reactivate`),
};

// SAF-T API
export const saftAPI = {
  import: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/saft/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: options,
    });
  },
  export: (dateFrom, dateTo) => api.get('/saft/export', { 
    params: { date_from: dateFrom, date_to: dateTo },
    responseType: 'blob'
  }),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  getPosConfig: () => api.get('/settings/pos-config'),
  getVendusActive: () => api.get('/settings/vendus-active'),
  update: (data) => api.put('/settings', data),
  addVendusLocation: (data) => api.post('/settings/vendus-location', data),
  deleteVendusLocation: (id) => api.delete(`/settings/vendus-location/${id}`),
  getVendusRegisters: (apiKey) => api.get('/settings/vendus-registers', { params: { api_key: apiKey } }),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Orders (Encomendas) API
export const ordersAPI = {
  getAll: (params = {}) => api.get('/orders', { params }),
  getPending: () => api.get('/orders/pending'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  convertToSale: (id) => api.post(`/orders/${id}/convert-to-sale`),
  markConverted: (id, saleId) => api.put(`/orders/${id}/mark-converted`, null, { params: { sale_id: saleId } }),
  cancel: (id) => api.delete(`/orders/${id}`),
  getReceipt: (id) => api.get(`/orders/${id}/receipt`),
};

// Returns (Devoluções) API
export const returnsAPI = {
  getAll: (params = {}) => api.get('/returns', { params }),
  getById: (id) => api.get(`/returns/${id}`),
  getSaleByCode: (saleCode) => api.get(`/returns/by-sale-code/${encodeURIComponent(saleCode)}`),
  getSaleByAtcud: (atcud) => api.get(`/returns/by-atcud/${encodeURIComponent(atcud)}`),
  getSaleById: (saleId) => api.get(`/returns/by-sale-id/${encodeURIComponent(saleId)}`),
  searchSales: (query) => api.get('/returns/search-sales', { params: { q: query } }),
  create: (data) => api.post('/returns', data),
  getReceipt: (id) => api.get(`/returns/${id}/receipt`),
  getReceiptPDF: (id) => api.get(`/returns/${id}/receipt-pdf`, {
    responseType: 'blob'
  }),
  getVendusCreditNotePDF: (id) => api.get(`/returns/${id}/vendus-credit-note-pdf`, {
    responseType: 'blob'
  }),
  getReconciliationReport: (params = {}) => api.get('/returns/reconciliation-report', { params }),
  getReconciliationReportCSV: (params = {}) => api.get('/returns/reconciliation-report.csv', {
    params,
    responseType: 'blob'
  }),
  getReconciliationReportPDF: (params = {}) => api.get('/returns/reconciliation-report.pdf', {
    params,
    responseType: 'blob'
  }),
  getReconciliationReportReceiptPDF: (params = {}) => api.get('/returns/reconciliation-report-receipt.pdf', {
    params,
    responseType: 'blob'
  }),
  retryVendusCreditNote: (id) => api.post(`/returns/${id}/retry-vendus-credit-note`),
};

// Reports API
export const reportsAPI = {
  getProvidersFinancial: (params = {}) => api.get('/reports/providers/financial', { params }),
  exportProvidersFinancial: (params = {}) => api.get('/reports-export/providers-financial', { params, responseType: 'blob' }),
  exportSalesSummaryPDF: (params = {}) => api.get('/reports-export/sales-summary-pdf', { params, responseType: 'blob' }),
  exportProfitLossPDF: (params = {}) => api.get('/reports-export/profit-loss-pdf', { params, responseType: 'blob' }),
  exportFinancialOverviewPDF: (params = {}) => api.get('/reports-export/financial-overview-pdf', { params, responseType: 'blob' }),
  exportTaxOverviewPDF: (params = {}) => api.get('/reports-export/tax-overview-pdf', { params, responseType: 'blob' }),
  exportProvidersFinancialPDF: (params = {}) => api.get('/reports-export/providers-financial-pdf', { params, responseType: 'blob' }),
  getFinancialOverview: (params = {}) => api.get('/reports/financial-overview', { params }),
  getTaxOverview: (params = {}) => api.get('/reports/tax-overview', { params }),
  getDashboardSummary: () => api.get('/reports/dashboard-summary'),
  getDailySales: (date) => api.get('/reports/sales/daily', { params: { date } }),
  getPeriodSales: (dateFrom, dateTo) => api.get('/reports/sales/period', { params: { date_from: dateFrom, date_to: dateTo } }),
  getOperatorsPerformance: (dateFrom, dateTo) => api.get('/reports/operators/performance', { params: { date_from: dateFrom, date_to: dateTo } }),
  getCashCloses: (dateFrom, dateTo) => api.get('/reports/cash-closes', { params: { date_from: dateFrom, date_to: dateTo } }),
  getProductMargins: () => api.get('/reports/products/margins'),
  getInventoryValue: () => api.get('/reports/inventory/value'),
  getExpiryRisk: (days = 90) => api.get('/reports/expiry-risk', { params: { days } }),
  exportExpiryRiskCSV: (days = 90) => api.get('/reports-export/expiry-risk-csv', { params: { days }, responseType: 'blob' }),
  exportExpiryRiskPDF: (days = 90) => api.get('/reports-export/expiry-risk-pdf', { params: { days }, responseType: 'blob' }),
  setExpiryRiskAction: (data) => api.post('/reports/expiry-risk/action', data),
  clearExpiryRiskAction: (productId, lotNumber) => api.delete('/reports/expiry-risk/action', { params: { product_id: productId, lot_number: lotNumber } }),
  getPrepaymentsSummary: () => api.get('/reports/prepayments/summary'),
  getPrepaymentsDetail: () => api.get('/reports/prepayments/detail'),
  rebuildPrepaymentsLedger: () => api.post('/reports/prepayments/rebuild-ledger'),
  getPrepaymentsDaySummary: (date) => api.get('/reports/prepayments/day-summary', { params: { date } })
};

export const cashMovementsAPI = {
  create: (data) => api.post('/cash-movements', data),
  getAll: (params = {}) => api.get('/cash-movements', { params }),
  getSessionStatus: () => api.get('/cash-movements/session-status'),
  getUnclosedSessionAlert: () => api.get('/cash-movements/unclosed-session-alert'),
  getOpenSessions: () => api.get('/cash-movements/open-sessions'),
  supervisorClose: (operatorId, data) => api.post(`/cash-movements/operators/${operatorId}/supervisor-close`, data),
  getConsolidatedSummary: (date) => api.get('/cash-movements/consolidated-summary', { params: { date } }),
  consolidatedClose: (data) => api.post('/cash-movements/consolidated-close', data),
  getSessionSummary: (params = {}) => api.get('/cash-movements/session-summary', { params }),
  getHistory: (params = {}) => api.get('/cash-movements/history', { params }),
};


// Internal Chat API

// Communications SMS API
export const communicationsSmsAPI = {
  getStatus: () => api.get('/communications/sms/status'),
  getAll: (params = {}) => api.get('/communications/sms', { params }),
  send: (data) => api.post('/communications/sms/send', data),
  syncInbox: () => api.post('/communications/sms/sync-inbox'),
  syncStatus: (limit = 100) => api.post('/communications/sms/sync-status', null, { params: { limit } }),
  searchCustomers: (q, limit = 80) => api.get('/communications/sms/customers-search', { params: { q, limit } }),
  resend: (id) => api.post(`/communications/sms/${id}/resend`),
  deleteLocal: (id) => api.delete(`/communications/sms/${id}`),
  markRead: (id, read = true) => api.put(`/communications/sms/${id}/read`, null, { params: { read } }),
};

export const internalChatAPI = {
  ping: () => api.post('/internal-chat/ping'),
  getUsers: () => api.get('/internal-chat/users'),
  getUnreadCount: () => api.get('/internal-chat/unread-count'),
  getThreads: () => api.get('/internal-chat/threads'),
  getMessages: (threadId, limit = 80) => api.get(`/internal-chat/threads/${threadId}/messages`, { params: { limit } }),
  sendMessage: (recipientId, body) => api.post('/internal-chat/messages', { recipient_id: recipientId, body }),
};

// Labels API
export const labelsAPI = {
  generate: (productId, batchId, quantity = 1) =>
    api.post('/labels/generate', null, { params: { product_id: productId, batch_id: batchId, quantity } }),
  generatePDF: (productId, batchId, quantity = 1, companyName = 'FAMASAÚDE ESPAÇO SAÚDE') =>
    api.post('/labels/generate-pdf', null, { 
      params: { product_id: productId, batch_id: batchId, quantity, company_name: companyName },
      responseType: 'blob'
    }),
};

export default api;

// Monthly Timesheets API
export const monthlyTimesheetsAPI = {
  getAll: (params = {}) => api.get('/monthly-timesheets', { params }),
  getById: (id) => api.get(`/monthly-timesheets/${id}`),
  create: (data) => api.post('/monthly-timesheets', data),
  update: (id, data) => api.put(`/monthly-timesheets/${id}`, data),
  approve: (id) => api.put(`/monthly-timesheets/${id}/approve`),
  getReportPDF: (id) => api.get(`/monthly-timesheets/${id}/report-pdf`, { responseType: 'blob' }),
};

// Resolve utentes pelo telefone (quando há familiares com o mesmo número)
export const resolveCustomerByPhone = (phone) => api.get('/customers/resolve-by-phone', { params: { phone } });
